import { NgRedux } from '@angular-redux/store';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { IAppState } from 'src/app/shared/state-management/state-interfaces';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { PageName } from 'src/app/shared/utils/user-roles';
import {
  Utility,
  convertParseDateTime,
  convertParseDateTimeLangSpecific,
  extractDateTimeFromDateString,
} from 'src/app/shared/utils/utility.service';
import { AddUserComponent } from 'src/app/user-management/add-user/add-user.component';
import { UserGroupComponent } from 'src/app/user-management/user-group/user-group.component';
import { IUser } from 'src/app/user-management/user-management-interfaces';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { IActiveIssuesCard } from '../activity/user-card.interface';
import { DashboardManagementService } from 'src/app/dashboard/dashboard-management.service';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { SharedActiveIssuesFilterComponent } from './shared-active-issues-filter/shared-active-issues-filter.component';
import {
  IActiveIssuesFilter,
  IEventCodes,
} from 'src/app/shared/models/data-interfaces';
import { eventCodes } from 'src/app/shared/utils/event-codes';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';
import { fr, enUS } from 'date-fns/locale';

@Component({
  selector: 'app-shared-active-issues-list',
  templateUrl: './shared-active-issues-list.component.html',
  styleUrls: ['./shared-active-issues-list.component.scss'],
})
export class SharedActiveIssuesListComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(SearchFieldComponent) searchFieldComponentRefComp:
    | SearchFieldComponent
    | undefined;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  private scrollListener!: (event: Event) => void;

  isFetching = false;
  shareedDataSubscription?: Subscription;
  readonly LANG_CONST = LANG_CONST;
  CONSTANTS = CONSTANTS;
  activeIssuesList$: IActiveIssuesCard[] = [];
  currentAlphabet: string | undefined;
  sharedData?: any;
  siteId!: string;
  id: string | undefined;
  userGroupId: string | undefined;
  PageName = PageName;
  searchId = 'search-user';
  searchKeyPress: boolean = false;
  userListsubscription: Subscription | undefined;
  subscription: Subscription | undefined;
  private readonly searchSubject$ = new Subject<string | undefined>();
  rolesSelected: string[] | undefined = [];
  filterCount: number = 0;
  filter: any = [];
  searchedWord: string = '';
  pageSize = 100;
  totalIssuesCount: number = 0;
  activeIssuesFilter!: IActiveIssuesFilter;
  eventCodes: IEventCodes[] = eventCodes;
  filterInitialized: boolean = false;
  activeIssuesActivitySubscription!: Subscription;
  locale = this.stateManagementService.getUserLanguage();
  private activeIssuesRequestSub?: Subscription;

  /**
   * @returns navigate to unit detail page
   * @void {srting}
   */
  navigateToUnitDetail(activeIssue: any): any[] {
    return [
      'unit',
      this.encode(activeIssue?.id),
      this.encode(activeIssue?.buildingId),
      this.encode(activeIssue?.unitId),
      this.encode(activeIssue?.buildingName),
      this.encode(activeIssue?.floorId),
    ];
  }

  /**
   * @returns navigate to access point detail page
   * @void {srting}
   */
  navigateToAccessPointDetail(activeIssue: any): any[] {
    return [
      'accesspoint',
      this.encode(activeIssue.id),
      this.encode(activeIssue.buildingId),
      this.encode(activeIssue.accessPointId),
      this.encode(activeIssue.buildingName),
      this.encode(activeIssue.floorId),
    ];
  }

  constructor(
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    private ngRedux: NgRedux<IAppState>,
    private router: Router,
    private route: ActivatedRoute,
    private dashboardManagementService: DashboardManagementService,
    private stateManagementService: StateManagementService,
    private localizationService: LocalizationService,
    private translate: TranslateService
  ) {
    this.activeIssuesActivitySubscription = this.ngRedux
      .select('activeIssuesActivity')
      .subscribe((activeIssueList: any) => {
        if (
          activeIssueList &&
          JSON.stringify(activeIssueList[0]?.issues) !==
            JSON.stringify(this.activeIssuesList$)
        ) {
          if (!this.searchedWord && this.filterCount === 0) {
            this.totalIssuesCount = this.activeIssuesList$?.length || 0;
          }
        }
      });

    this.initializeFilter();
  }

  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.shareedDataSubscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
        const currentSiteId = this.sharedData?.selectedSite?.siteId;

        if (currentSiteId && currentSiteId !== this.siteId) {
          this.resetDataForNewSite(currentSiteId);
        }
      }
    );

    this.searchSubject$
      .pipe(debounceTime(200))
      .subscribe((searchQuery: any) => {
        this.searchedWord = searchQuery;
        this.callGetUsersAPI();
      });
  }

  resetDataForNewSite(newSiteId: string): void {
    this.siteId = newSiteId;
    this.activeIssuesList$ = [];
    this.totalIssuesCount = 0;
    this.filterCount = 0;
    this.searchedWord = '';
    this.searchFieldComponentRefComp?.clear();
    this.initializeFilter();
    this.isFetching = false;

    if (this.activeIssuesList$.length < this.pageSize) {
      this.getActiveIssues();
    }
  }

  ngAfterViewInit(): void {
    if (this.scrollContainer) {
      if (window.__dev__) console.log('scrollContainer:', this.scrollContainer);
      this.scrollContainer.nativeElement.addEventListener(
        'scroll',
        (event: Event) => this.onContainerScroll(event)
      );
    } else {
      console.error('scrollContainer is not defined');
    }
  }

  loadMore(): void {
    if (this.isFetching) return;

    if (window.__dev__) console.log('Fetching more items...');
    this.getActiveIssues();
  }

  onContainerScroll(event: Event): void {
    const container = this.scrollContainer.nativeElement;

    if (window.__dev__) {
      console.log('ScrollHeight:', container.scrollHeight);
      console.log('ScrollTop:', container.scrollTop);
      console.log('ClientHeight:', container.clientHeight);
    }

    const atBottom =
      Math.abs(
        container.scrollHeight - container.scrollTop - container.clientHeight
      ) <= 1;

    if (window.__dev__) console.log('At Bottom:', atBottom);

    if (
      atBottom &&
      !this.isFetching &&
      this.activeIssuesList$.length < this.totalIssuesCount
    ) {
      if (window.__dev__) console.log('Fetching next page...');
      this.getActiveIssues();
    }
  }

  /**
   * @description get dashobard statistic
   * @param siteId
   * @returns void
   */
  getActiveIssues(
    buildingId?: string,
    floorId?: string,
    wingName?: string,
    eventCodes?: string[]
  ): void {
    if (this.isFetching) return;
    this.isFetching = true;

    // Ensure cursor is a valid array before sending
    const cursor = Array.isArray(this.activeIssuesFilter?.pagination?.cursor)
      ? this.activeIssuesFilter.pagination.cursor
      : [];

    const filterCriteria: any = {
      searchEntity: JSON.stringify({ deviceName: this.searchedWord }),
      pagination: {
        cursor,
        size: this.pageSize,
      },
    };

    if (buildingId) filterCriteria.buildingId = buildingId;
    if (floorId) filterCriteria.floorId = floorId;
    if (wingName && wingName !== this.translate.instant('NOT_SPECIFIED'))
      filterCriteria.wingName = wingName;
    if (eventCodes?.length) filterCriteria.eventCode = eventCodes;

    if (window.__dev__) {
      console.log('Filter Criteria:', filterCriteria);
      console.log('Site ID:', this.siteId);
      console.log('Sending Cursor:', JSON.stringify(cursor));
    }

    this.activeIssuesRequestSub = this.dashboardManagementService
      .getActiveIssues({
        siteId: this.siteId,
        hideLoader: true,
        sync: true,
        filterCriteria,
      })
      .subscribe(
        (res: any) => {
          this.isFetching = false;
          this.searchKeyPress = false;

          if (window.__dev__) console.log('response:', res);

          if (res[0]?.issues?.length) {
            const newIssues = res[0].issues;

            // Merge new issues with existing ones and remove duplicates
            const mergedIssues = [...this.activeIssuesList$, ...newIssues];
            this.activeIssuesList$ = [
              ...new Map(
                mergedIssues.map((issue) => [issue.id, issue])
              ).values(),
            ];

            this.totalIssuesCount = res[0].totalCount || 0;

            // Validate cursor from response
            this.activeIssuesFilter.pagination.cursor = Array.isArray(
              res[0]?.cursor
            )
              ? [...res[0].cursor]
              : [];

            if (window.__dev__) {
              console.log(
                'Received Cursor:',
                JSON.stringify(this.activeIssuesFilter.pagination.cursor)
              );
            }
          }
        },
        (error: any) => {
          console.error('Error fetching active issues:', error);
          this.isFetching = false;
        }
      );
  }

  private resetPagination(): void {
    this.activeIssuesList$ = [];
    this.totalIssuesCount = 0;

    if (this.activeIssuesFilter?.pagination) {
      this.activeIssuesFilter.pagination.cursor = [];
    }
  }

  /**
   *
   * @param time get time format
   * @returns return formated time
   */
  getContentFormatDate(time: string, format: 'hh:mma') {
    const lang = this.locale === LANG_CONST.FRENCH ? fr : enUS;

    return convertParseDateTimeLangSpecific(
      extractDateTimeFromDateString(String(time)),
      "yyyy-MM-dd'T'HH:mm:ss",
      format,
      lang
    );
  }

  checkIfNew(name: string) {
    if (this.currentAlphabet === name?.charAt(0).toLowerCase()) return false;
    this.currentAlphabet = name?.charAt(0).toLowerCase();
    return true;
  }

  /**
   * @description: add new user
   * @inut {none}
   * @output {void}
   */
  addUser(): void {
    const dialogInstance = this.helperService.openModalDialog(
      AddUserComponent,
      {
        isEditable: false,
        enableBackOperation: false,
        defaultNavigation: CONSTANTS.BUILDING,
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
  }

  /**
   * @description: add new user group
   * @inut {none}
   * @output {void}
   */
  addUserGroup(): void {
    const dialogInstance = this.helperService.openModalDialog(
      UserGroupComponent,
      {
        isEditable: false,
        enableBackOperation: false,
        defaultNavigation: CONSTANTS.BUILDING,
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
  }

  encode(data: string): string {
    return Utility.encodeToBase64(data);
  }

  searchThis(data: string) {
    this.searchKeyPress = true;
    this.searchSubject$.next(data);
  }

  trackByActiveIssues(index: number, user: IUser) {
    return user;
  }

  /**
   * @description: #callGetUserAPI function will call an API for getUsersBySite
   * @inut {none}
   * @output {void}
   */
  callGetUsersAPI(): void {
    this.activeIssuesRequestSub?.unsubscribe();
    this.isFetching = false;
    this.resetPagination();

    const { buildingIds, floorIds, wingsDescription, eventCodeIds } =
      this.activeIssuesFilter;

    this.getActiveIssues(
      buildingIds?.[0],
      floorIds?.[0],
      wingsDescription?.[0],
      eventCodeIds
    );
  }

  /**
   * @description: ##selectFilter function opens the modal poup for Filter issues activity.
   * @input none
   * @output { void }
   */
  selectFilter() {
    const dialogRef = this.helperService.openModalDialog(
      SharedActiveIssuesFilterComponent,
      {
        siteId: this.siteId,
        activeIssuesFilter: this.activeIssuesFilter,
        filterInitialized: this.filterInitialized,
      },
      [CONSTANTS.FIXED_SCREEN_MODAL]
    );

    dialogRef
      ?.afterClosed()
      .subscribe(
        (result: {
          activeIssuesFilter: IActiveIssuesFilter;
          filterInitialized: boolean;
        }) => {
          if (!result) return;

          this.activeIssuesFilter = {
            ...result.activeIssuesFilter,
            pagination: {
              ...result.activeIssuesFilter.pagination,
              cursor: [],
            },
          };

          this.resetPagination();

          const { buildingIds, floorIds, wingsDescription, eventCodeIds } =
            this.activeIssuesFilter;

          this.getActiveIssues(
            buildingIds?.[0],
            floorIds?.[0],
            wingsDescription?.[0],
            eventCodeIds
          );

          this.filterCount = this.getFilterCount(this.activeIssuesFilter);
          this.filterInitialized = result.filterInitialized;

          this.router.navigate(['/dashboard/active-issues']);
        }
      );
  }

  /**
   * @description Calculates the total count of items in the active issues filter.
   * This count includes the length of buildingDescription, floorDescription, wingsDescription,
   * and eventDescription arrays.
   *
   * @param {IActiveIssuesFilter} activeIssuesFilter - The active issues filter object.
   * @returns {number} The total count of items in the active issues filter.
   */
  getFilterCount(activeIssuesFilter: IActiveIssuesFilter): number {
    return (
      (activeIssuesFilter?.buildingDescription?.length || 0) +
      (activeIssuesFilter?.floorDescription?.length || 0) +
      (activeIssuesFilter?.wingsDescription?.length || 0) +
      (activeIssuesFilter?.eventDescription?.length || 0)
    );
  }

  /**
   * @description #initializeFilter function gets the buildingsList and
   *  floorList from app and
   *               map the data int the required state of filter component.
   * @returns void
   */

  initializeFilter() {
    this.filterInitialized = false;
    this.activeIssuesFilter = {
      buildings: [],
      floors: [],
      wings: [],
      eventCodes: this.eventCodes,
      buildingDescription: [],
      floorDescription: [],
      wingsDescription: [],
      eventDescription: [],
      buildingIds: [],
      floorIds: [],
      eventCodeIds: [],
      pagination: {
        cursor: [],
        size: 25,
      },
    };
  }

  ngOnDestroy(): void {
    this.shareedDataSubscription?.unsubscribe();
    this.userListsubscription?.unsubscribe();
    this.activeIssuesRequestSub?.unsubscribe();
    this.totalIssuesCount = 0;
    this.stateManagementService.updateActiveIssues(undefined);
    this.activeIssuesActivitySubscription.unsubscribe();

    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.removeEventListener(
        'scroll',
        this.scrollListener
      );
    }
  }
}
